<template>
  <q-dialog v-model="dialog" persistent maximized transition-show="slide-up" transition-hide="slide-down">
    <q-card style="background-color: #f5f7fa;">
      <q-bar class="bg-primary text-white q-py-md">
        <div class="text-subtitle1 text-weight-bold">
          <i class="fas fa-id-card q-mr-sm"></i>
          Employee 201 Record Preview - {{ fullName }}
        </div>
        <q-space />
        <q-btn dense flat icon="close" v-close-popup>
          <q-tooltip content-class="bg-white text-primary">Close</q-tooltip>
        </q-btn>
      </q-bar>

      <q-card-section v-if="loading" class="flex flex-center" style="height: 80vh;">
        <q-spinner-dots color="primary" size="40px" />
      </q-card-section>

      <q-card-section v-else class="q-pa-lg">
        <div class="row q-col-gutter-md">
          <!-- Left Sidebar Profile Card -->
          <div class="col-xs-12 col-md-3">
            <q-card class="no-shadow border-radius-lg bg-white q-pa-md text-center">
              <q-avatar size="140px" class="shadow-2 q-mb-md">
                <img :src="avatarSrc" />
              </q-avatar>
              <div class="text-h6 text-weight-bold text-grey-9">{{ fullName }}</div>
              <div class="text-subtitle2 text-primary text-weight-medium q-mb-xs">{{ record.position || 'No Position' }}</div>
              <q-chip dense :color="statusColor" text-color="white" class="q-px-md text-weight-bold">
                {{ (record.is_active || 'inactive').toUpperCase() }}
              </q-chip>

              <q-separator class="q-my-md" />

              <div class="text-left q-gutter-y-sm text-grey-8">
                <div>
                  <i class="fas fa-hashtag q-mr-sm text-grey-6"></i>
                  <strong>ID:</strong> {{ record.employee_id }}
                </div>
                <div>
                  <i class="fas fa-envelope q-mr-sm text-grey-6"></i>
                  <strong>Email:</strong> {{ record.email || 'N/A' }}
                </div>
                <div>
                  <i class="fas fa-phone-alt q-mr-sm text-grey-6"></i>
                  <strong>Phone:</strong> {{ record.mobile_number || 'N/A' }}
                </div>
                <div>
                  <i class="fas fa-calendar-alt q-mr-sm text-grey-6"></i>
                  <strong>Hired:</strong> {{ record.date_hired || 'N/A' }}
                </div>
              </div>
            </q-card>
          </div>

          <!-- Right Content Tabs -->
          <div class="col-xs-12 col-md-9">
            <q-card class="no-shadow border-radius-lg bg-white">
              <q-tabs v-model="tab" dense class="text-grey-7 bg-grey-1" active-color="primary" indicator-color="primary" align="justify" narrow-indicator>
                <q-tab name="personal" label="Personal Info" />
                <q-tab name="employment" label="Employment"  />
                <q-tab name="salary" label="Compensation" />
                <q-tab name="government" label="Gov & Benefits" />
              </q-tabs>

              <q-separator />

              <q-tab-panels v-model="tab" animated class="q-pa-md">
                <!-- Personal Info Tab -->
                <q-tab-panel name="personal" class="q-gutter-y-md">
                  <div class="text-subtitle1 text-weight-bold text-primary">
                    <i class="fas fa-user-circle q-mr-sm"></i>Personal Information
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="First Name" v-model="record.firstname" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Middle Name" v-model="record.middlename" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Last Name" v-model="record.lastname" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Date of Birth" v-model="record.date_of_birth" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Gender" v-model="record.gender" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Civil Status" v-model="record.civil_status" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Permanent Address" v-model="record.address" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Present/City Address" v-model="record.city_address" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Landline Number" v-model="record.landline_number" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Mobile Number" v-model="record.mobile_number" readonly outlined dense />
                    </div>
                  </div>

                  <div class="text-subtitle1 text-weight-bold text-primary q-mt-lg">
                    <i class="fas fa-graduation-cap q-mr-sm"></i>Education Background
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Educational Attainment" v-model="record.educational_attainment" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="College Course" v-model="record.college_course" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="College School" v-model="record.college_school" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="High School" v-model="record.high_school" readonly outlined dense />
                    </div>
                  </div>

                  <div class="text-subtitle1 text-weight-bold text-primary q-mt-lg">
                    <i class="fas fa-first-aid q-mr-sm"></i>Emergency Contact
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Person to Notify" v-model="record.person_to_notif" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Contact Number" v-model="record.person_to_notif_contact" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Relationship" v-model="record.relation_to_person_to_notif" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Email Address" v-model="record.email_of_person_to_notif" readonly outlined dense />
                    </div>
                  </div>
                </q-tab-panel>

                <!-- Employment Tab -->
                <q-tab-panel name="employment" class="q-gutter-y-md">
                  <div class="text-subtitle1 text-weight-bold text-primary">
                    <i class="fas fa-briefcase q-mr-sm"></i>Company & Job Details
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Company Name" v-model="record.companyname" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Branch" v-model="record.branch" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Department" v-model="record.department" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Position" v-model="record.position" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Site Location (LGU)" v-model="record.location" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Date Hired" v-model="record.date_hired" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Account" v-model="record.account" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Cost Center" v-model="record.cost_center" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Current Section" v-model="record.c_section" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Rank" v-model="record.rank" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Employment Status" v-model="record.employment_status" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Payroll Group / Label" v-model="record.payroll_group" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Immediate Head" v-model="record.immediate_head" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Division Head" v-model="record.division_head" readonly outlined dense />
                    </div>
                  </div>

                  <div class="text-subtitle1 text-weight-bold text-primary q-mt-lg" v-if="record.is_active === 'hold'">
                    <i class="fas fa-hand-paper q-mr-sm"></i>Hold Information
                  </div>
                  <q-separator v-if="record.is_active === 'hold'" />
                  <div class="row q-col-gutter-sm" v-if="record.is_active === 'hold'">
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Hold Date" v-model="record.hold_date" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Hold Effectivity" v-model="record.hold_effectivity" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Hold Duration" v-model="record.hold_duration" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Hold Reason" v-model="record.hold_reason" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Hold Period" v-model="record.hold_period" readonly outlined dense />
                    </div>
                  </div>

                  <div class="text-subtitle1 text-weight-bold text-primary q-mt-lg">
                    <i class="fas fa-history q-mr-sm"></i>Previous Employment
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Previous Company" v-model="record.previous_company" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Length of Stay" v-model="record.previous_company_length_of_stay" readonly outlined dense />
                    </div>
                  </div>
                </q-tab-panel>

                <!-- Compensation & Bank Tab -->
                <q-tab-panel name="salary" class="q-gutter-y-md">
                  <div class="text-subtitle1 text-weight-bold text-primary">
                    <i class="fas fa-money-bill-wave q-mr-sm"></i>Salary Details
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Basic Salary" v-model="record.basic_salary" readonly outlined dense prefix="₱" />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Allowance" v-model="record.allowance" readonly outlined dense prefix="₱" />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Training Allowance" v-model="record.training_allowance" readonly outlined dense prefix="₱" />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="De Minimis" v-model="record.deminimis" readonly outlined dense prefix="₱" />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Total Salary" v-model="record.total_salary" readonly outlined dense prefix="₱" />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Period Rate" v-model="record.period_rate" readonly outlined dense />
                    </div>
                  </div>

                  <div class="text-subtitle1 text-weight-bold text-primary q-mt-lg">
                    <i class="fas fa-university q-mr-sm"></i>Bank Details
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Payment Mode" v-model="record.payment_mode" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Paying Bank" v-model="record.paying_bank" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Account Number" v-model="record.account_number" readonly outlined dense />
                    </div>
                  </div>
                </q-tab-panel>

                <!-- Gov & Benefits Tab -->
                <q-tab-panel name="government" class="q-gutter-y-md">
                  <div class="text-subtitle1 text-weight-bold text-primary">
                    <i class="fas fa-landmark q-mr-sm"></i>Government Contributions
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="SSS Number" v-model="record.sss_number" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="HDMF (Pag-IBIG) Number" v-model="record.hdmf_number" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="PhilHealth Number" v-model="record.philhealth_number" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="TIN" v-model="record.tin_number" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="BIR Number" v-model="record.bir_number" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-4">
                      <q-input label="Tax Status" v-model="record.tax_status" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Pay Class" v-model="record.pay_class" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Accountability" v-model="record.accountability" readonly outlined dense />
                    </div>
                  </div>

                  <div class="text-subtitle1 text-weight-bold text-primary q-mt-lg">
                    <i class="fas fa-shield-alt q-mr-sm"></i>Health Card & Insurance
                  </div>
                  <q-separator />
                  <div class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Healthcard Provider" v-model="record.healthcard_name" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Healthcard Number" v-model="record.healthcard_number" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Insurance Provider" v-model="record.insurance_name" readonly outlined dense />
                    </div>
                    <div class="col-xs-12 col-sm-6">
                      <q-input label="Insurance Number" v-model="record.insurance_number" readonly outlined dense />
                    </div>
                  </div>
                </q-tab-panel>
              </q-tab-panels>
            </q-card>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';

export default {
  name: 'PreviewModal',
  data() {
    return {
      dialog: false,
      loading: false,
      tab: 'personal',
      record: {},
      apiUrl: process.env.VUE_APP_API_URL,
      avatarSrc: ''
    };
  },
  mixins: [mixins],
  computed: {
    fullName() {
      if (!this.record.firstname) return 'Employee Profile';
      const middle = this.record.middlename ? ` ${this.record.middlename.charAt(0)}.` : '';
      return `${this.record.firstname}${middle} ${this.record.lastname}`;
    },
    statusColor() {
      const status = (this.record.is_active || '').toLowerCase();
      if (status === 'active') return 'green';
      if (status === 'hold') return 'orange';
      return 'red';
    }
  },
  created() {
    this.eventHub.$on('PreviewEmployee', this.openPreview);
  },
  beforeDestroy() {
    this.eventHub.$off('PreviewEmployee', this.openPreview);
  },
  methods: {
    openPreview(payload) {
      // Guard: if same employee is already loaded and dialog is open, just show it without refetching
      if (payload && payload.id && this.record && this.record.id === payload.id && this.dialog) {
        this.dialog = true;
        return;
      }
      if (!payload || !payload.id) {
        this.$q.notify({
          message: 'Invalid payload for preview.',
          color: 'negative',
          position: 'center'
        });
        return;
      }

      this.dialog = true;
      this.loading = true;
      this.tab = 'personal';

      axios.get(this.apiUrl + '/employee/preview-201', {
        headers: { 'Authorization': `Bearer ${this.token}` },
        params: { id: payload.id }
      })
        .then(res => {
          if (res.data.status) {
            this.record = res.data.data;
            // Set avatar source, fallback to placeholder if image missing
            this.avatarSrc = this.record.image ? (this.apiUrl + this.record.image) : '../../assets/boy-avatar.png';
          } else {
            this.$q.notify({
              message: res.data.message || 'Failed to load employee preview.',
              color: 'negative',
              position: 'center'
            });
            this.dialog = false;
          }
        })
        .catch(err => {
          console.error(err);
          this.$q.notify({
            message: 'Error connecting to the server.',
            color: 'negative',
            position: 'center'
          });
          this.dialog = false;
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.border-radius-lg {
  border-radius: 12px;
}
.shadow-2 {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}
</style>
